import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ActivityChatInsumosPicker,
  ActivityChatMembersPicker,
  ActivityFormChatSummaryCard,
  ACTIVITY_STEP_ORDER,
  EventChatLocationPicker,
  mapRawMemberToActivityChatOption,
  type ActivityChatMemberOption,
  type ActivityChatMembersConfirmPayload,
  type ActivityChatSummary,
  type EventChatLocationState,
  type FormChatEditableFieldOption,
  type FormChatMessage,
  type RawActivityAvailableMember,
} from "gsintegrations";
import {
  trackActivitiesError,
  trackActivitiesEvent,
} from "../analytics/activities";
import { useSubscription } from "../auth/SubscriptionContext";
import { useCachedMedium } from "../hooks/useCachedMedium";
import { useCachedTerreiro } from "../hooks/useCachedTerreiro";
import { useFormChatSubmitActivity } from "../hooks/useFormChatSubmitActivity";
import { useItemAndMediunsQueries } from "../hooks/useItemAndMediunsQueries";
import { useProductAnalyticsBase } from "../hooks/useProductAnalyticsContext";
import type {
  FormChatMessageRequest,
  FormChatMessageResponse,
  FormChatState,
  ActivityReferenceData,
} from "../repositories/formChat.repository";
import { formChatRepository } from "../repositories/formChat.repository";
import { extractAxiosApiError, toSafeInventoryQuantity } from "../utils/extractAxiosApiError";
import { buildActivityFormChatPayload } from "../utils/activityFormChatPayloadBuilder";
import {
  formatActivityChatLocationLabel,
  isActivityLocationPayload,
  resolveHiddenActivityLocationFields,
} from "../utils/activityChatLocationFields";
import { isTerreiroAddressConfigured } from "../utils/eventLocationAddress";
import { createEventQuery } from "./useCreateEventQueries";
import { terreiroRepository } from "../repositories/terreiroRepository";
import type { ActivityType } from "../types/activity.types";
import { getActivityTypeWording } from "../utils/activityTypeRoutes";
import { normalizeActivityFormChatState } from "../utils/normalizeActivityFormChatState";
import {
  CHECKIN_WINDOW_EDIT_FIELDS,
  formatChatActivityType,
  formatChatDate,
  formatChatMinutes,
  formatChatText,
  formatChatTime,
  formatChatToggle,
  formatChatYesNo,
  toInsumoSelection,
  toStringList,
} from "../utils/formChatEditPreview";

const ACTIVITY_TYPE_EDIT_FIELD = { key: "activityType", label: "Tipo" };

const ACTIVITY_EDITABLE_FIELDS: Array<{ key: string; label: string }> = [
  { key: "title", label: "Título" },
  { key: "description", label: "Descrição" },
  { key: "isTemplate", label: "Modelo" },
  { key: "date", label: "Data" },
  { key: "startTime", label: "Horário início" },
  { key: "endTime", label: "Horário término" },
  { key: "qrCheckinEnabled", label: "Check-in" },
  { key: "checkinStartWindowMinutes", label: "Check-in antes (min)" },
  { key: "checkinEndWindowMinutes", label: "Check-in depois (min)" },
  { key: "isInTerreiro", label: "No terreiro" },
  { key: "location", label: "Local" },
  { key: "memberIds", label: "Participantes" },
  { key: "minConfirmedMembers", label: "Quórum mínimo" },
  { key: "insumos", label: "Materiais" },
];

/** Personal activities always have the owner medium as the only participant. */
const PERSONAL_HIDDEN_EDIT_FIELDS = new Set(["memberIds", "minConfirmedMembers"]);

interface UseActivityFormChatOptions {
  sessionId?: string;
  messages: FormChatMessage[];
  activeField?: string;
  formState: FormChatState;
  summary?: ActivityChatSummary;
  descriptionAutoGenerated?: boolean;
  onResponse: (response: FormChatMessageResponse) => void;
  onUserMessage: (content: string) => void;
  onError?: (error: { code: string; message: string }) => void;
  onReset: () => void;
  onEdit?: () => void;
  onCloseAssistant?: () => void;
  /** Existing activity being edited; switches the chat from create to edit mode. */
  editTargetId?: string;
  /** Activity model the new activity is created from. */
  modelTargetId?: string;
  /** Set from the route; stamped on the payload so the user is never asked for it. */
  activityType?: ActivityType;
}

export function useActivityFormChatHelpers({
  sessionId,
  messages,
  activeField,
  formState,
  summary,
  descriptionAutoGenerated = false,
  onResponse,
  onUserMessage,
  onError,
  onReset,
  onEdit,
  onCloseAssistant,
  editTargetId,
  modelTargetId,
  activityType = "ACTIVITY",
}: UseActivityFormChatOptions) {
  const isEditMode = Boolean(editTargetId);
  const resolvedActivityType: ActivityType =
    formState.activityType === "RITUAL" || formState.activityType === "ACTIVITY"
      ? formState.activityType
      : activityType;
  const wording = getActivityTypeWording(resolvedActivityType);
  const editableFields = useMemo(
    () =>
      isEditMode
        ? [ACTIVITY_TYPE_EDIT_FIELD, ...ACTIVITY_EDITABLE_FIELDS]
        : ACTIVITY_EDITABLE_FIELDS,
    [isEditMode],
  );
  const productBase = useProductAnalyticsBase();
  const { id: terreiroId } = useCachedTerreiro() ?? {};
  const { id: mediumId } = useCachedMedium() ?? {};
  const isPersonalProfile = !terreiroId;
  const { hasFeature } = useSubscription();
  const { submitActivityByChat, updateActivityByChat } = useFormChatSubmitActivity();
  const { items = [], mediuns = [] } = useItemAndMediunsQueries(
    terreiroId,
    mediumId,
  );
  const { data: terreiroAddress } = useQuery({
    queryKey: createEventQuery.terreiroAddress(terreiroId),
    queryFn: () =>
      terreiroId
        ? terreiroRepository.getTerreiroAddress(terreiroId)
        : Promise.resolve(null),
    enabled: Boolean(terreiroId),
    staleTime: 1000 * 60 * 5,
  });
  const isTerreiroAddressEmpty =
    terreiroAddress !== undefined && !isTerreiroAddressConfigured(terreiroAddress);

  const memberOptions = useMemo(
    () =>
      (mediuns as RawActivityAvailableMember[]).map(
        mapRawMemberToActivityChatOption,
      ),
    [mediuns],
  );

  const activityReferenceData = useMemo<ActivityReferenceData>(
    () => ({
      members: memberOptions.map((member: ActivityChatMemberOption) => ({
        id: member.id,
        name: member.name,
      })),
      insumos: (items as Array<Record<string, unknown>>).map((item) => ({
        id: String(item.id),
        name: String(item.name ?? ""),
        availableQuantity: toSafeInventoryQuantity(
          item.quantity ?? item.availableQuantity,
        ),
        unit: String(item.unit ?? ""),
      })),
      ownerMediumId: mediumId,
      canCreateModels: hasFeature("canUseTemplates"),
    }),
    [hasFeature, items, memberOptions, mediumId],
  );

  const insumoOptions = useMemo(
    () =>
      (items as Array<Record<string, unknown>>).map((item) => ({
        id: String(item.id),
        name: String(item.name ?? ""),
        availableQuantity: toSafeInventoryQuantity(
          item.quantity ?? item.availableQuantity,
        ),
        unit: String(item.unit ?? ""),
        imageUrl: (item.imageUrl as string | null | undefined) ?? null,
      })),
    [items],
  );

  type ActivityMutationInput = {
    payload: Omit<FormChatMessageRequest, "entityType">;
    referenceData: ActivityReferenceData;
    isPersonalProfile: boolean;
    activityType: ActivityType;
  };

  const { mutateAsync: sendActivityMutation, isPending } = useMutation({
    mutationKey: ["form-chat", "activity-message"],
    mutationFn: async ({
      payload,
      referenceData,
      isPersonalProfile: personalProfile,
      activityType: requestActivityType,
    }: ActivityMutationInput): Promise<FormChatMessageResponse> => {
      if (!terreiroId && !mediumId) {
        throw new Error("Owner scope required");
      }

      const fullPayload: FormChatMessageRequest = {
        entityType: "activity",
        activityType: requestActivityType,
        referenceData,
        isPersonalProfile: personalProfile,
        ...(editTargetId
          ? { mode: "edit" as const, targetEntityId: editTargetId }
          : modelTargetId
            ? { mode: "from_model" as const, targetEntityId: modelTargetId }
            : {}),
        ...payload,
      };

      return terreiroId
        ? formChatRepository.sendMessage(terreiroId, fullPayload)
        : formChatRepository.sendPersonalMessage(mediumId!, fullPayload);
    },
    onError: (err) => {
      trackActivitiesError("activity_form_chat_submit_error", productBase, err);
    },
  });

  const sendActivityRequest = useCallback(
    async (
      payload: Omit<FormChatMessageRequest, "entityType">,
    ): Promise<FormChatMessageResponse | undefined> => {
      if (!terreiroId && !mediumId) return undefined;

      try {
        const response = await sendActivityMutation({
          payload,
          referenceData: activityReferenceData,
          isPersonalProfile,
          activityType: resolvedActivityType,
        });
        onResponse(response);
        return response;
      } catch (err: unknown) {
        const nextError = extractAxiosApiError(err);
        onError?.(nextError);
        throw err;
      }
    },
    [
      activityReferenceData,
      isPersonalProfile,
      mediumId,
      resolvedActivityType,
      onError,
      onResponse,
      sendActivityMutation,
      terreiroId,
    ],
  );

  const handleStructuredSelection = useCallback(
    async (
      field: "memberIds" | "insumos" | "location",
      value:
        | ActivityChatMembersConfirmPayload
        | Array<{ id: string; quantity: number }>
        | EventChatLocationState,
    ) => {
      const label =
        field === "memberIds"
          ? `Selecionados ${(value as ActivityChatMembersConfirmPayload).memberIds.length} participante(s) — quórum ${(value as ActivityChatMembersConfirmPayload).minConfirmedMembers}`
          : field === "location"
            ? formatActivityChatLocationLabel(value as EventChatLocationState)
            : `Selecionados ${(value as Array<{ id: string; quantity: number }>).length} material(is)`;

      onUserMessage(label);

      if (!sessionId && messages.length <= 1) {
        trackActivitiesEvent("activity_form_chat_started", productBase);
      }
      trackActivitiesEvent("activity_form_chat_field_collected", productBase);

      try {
        await sendActivityRequest({
          sessionId,
          message: "",
          structuredSelection:
            field === "memberIds"
              ? {
                  field,
                  memberIds: (value as ActivityChatMembersConfirmPayload)
                    .memberIds,
                  minConfirmedMembers: (
                    value as ActivityChatMembersConfirmPayload
                  ).minConfirmedMembers,
                }
              : field === "location"
                ? {
                    field,
                    location: value as EventChatLocationState,
                  }
                : {
                    field,
                    insumos: (value as Array<{ id: string; quantity: number }>).map(
                      (item) => ({
                        id: item.id,
                        quantity: Math.max(
                          1,
                          Math.floor(Number(item.quantity) || 1),
                        ),
                      }),
                    ),
                  },
        });
      } catch {
        // Error surfaced via onError.
      }
    },
    [messages.length, onUserMessage, productBase, sendActivityRequest, sessionId],
  );

  const handleConfirmActivity = useCallback(async () => {
    const actionLabel = isEditMode ? "salvar" : "criar";
    try {
      if (!summary) {
        onError?.({
          code: "FORM_CHAT_INCOMPLETE",
          message: isEditMode
            ? `Revise os dados ${wording.ofThe} ${wording.singular} no chat antes de salvar as alterações.`
            : `Revise os dados ${wording.ofThe} ${wording.singular} no chat antes de confirmar a criação.`,
        });
        return;
      }

      let mergedState = normalizeActivityFormChatState(
        formState as FormChatState,
        mediumId,
      );

      if (sessionId) {
        try {
          const response = await sendActivityRequest({
            sessionId,
            message: "",
            action: "confirm",
          });
          if (response?.formState) {
            mergedState = normalizeActivityFormChatState(
              { ...mergedState, ...response.formState },
              mediumId,
            );
          }
        } catch {
          // Review is already visible — keep going with the client-side state.
        }
      }

      const payload = buildActivityFormChatPayload({
        data: mergedState,
        terreiroId,
        mediumId,
        activityType: resolvedActivityType,
      });

      if (!payload) {
        onError?.({
          code: "FORM_CHAT_SCOPE_ERROR",
          message: `Não foi possível identificar o contexto para ${actionLabel} ${wording.article} ${wording.singular}.`,
        });
        return;
      }

      if (!payload.title) {
        onError?.({
          code: "FORM_CHAT_INCOMPLETE",
          message: `Informe o título ${wording.ofThe} ${wording.singular} antes de ${actionLabel}.`,
        });
        return;
      }

      if (!payload.isTemplate && !payload.date) {
        onError?.({
          code: "FORM_CHAT_INCOMPLETE",
          message: `Informe a data ${wording.ofThe} ${wording.singular} antes de ${actionLabel}.`,
        });
        return;
      }

      if (
        !mergedState.isTemplate &&
        mergedState.isAllDay !== true &&
        (!mergedState.startTime || !mergedState.endTime)
      ) {
        onError?.({
          code: "FORM_CHAT_INCOMPLETE",
          message: `Informe o horário ${wording.ofThe} ${wording.singular} antes de ${actionLabel}.`,
        });
        return;
      }

      if (editTargetId) {
        await updateActivityByChat({
          activityId: editTargetId,
          data: mergedState,
          activityType: resolvedActivityType,
        });
      } else {
        await submitActivityByChat({
          data: mergedState,
          modelId: modelTargetId,
          activityType: resolvedActivityType,
        });
      }
      onReset();
      onCloseAssistant?.();
    } catch (err: unknown) {
      const nextError = extractAxiosApiError(
        err,
        isEditMode
          ? "Não foi possível salvar as alterações. Revise os dados e tente novamente."
          : `Não foi possível criar ${wording.article} ${wording.singular}. Revise os dados e tente novamente.`,
      );
      onError?.(nextError);
    }
  }, [
    resolvedActivityType,
    editTargetId,
    formState,
    isEditMode,
    modelTargetId,
    updateActivityByChat,
    mediumId,
    onCloseAssistant,
    onError,
    onReset,
    sendActivityRequest,
    sessionId,
    submitActivityByChat,
    summary,
    terreiroId,
    wording,
  ]);

  const fieldPanel = useMemo(() => {
    if (activeField === "memberIds" && !isPersonalProfile) {
      return (
        <ActivityChatMembersPicker
          members={memberOptions}
          selectedIds={(formState.memberIds as string[]) ?? []}
          selectedMinConfirmedMembers={formState.minConfirmedMembers as number | undefined}
          isLoading={isPending}
          onConfirm={(payload: ActivityChatMembersConfirmPayload) =>
            handleStructuredSelection("memberIds", payload)
          }
        />
      );
    }
    if (activeField === "insumos") {
      return (
        <ActivityChatInsumosPicker
          insumos={insumoOptions}
          selected={(formState.insumos as Array<{ id: string; quantity: number }>) ?? []}
          isLoading={isPending}
          onConfirm={(insumos: Array<{ id: string; quantity: number }>) =>
            handleStructuredSelection("insumos", insumos)
          }
        />
      );
    }
    if (activeField === "location" && !isPersonalProfile) {
      const location = isActivityLocationPayload(formState.location)
        ? formState.location
        : undefined;
      return (
        <EventChatLocationPicker
          value={location}
          isTerreiroAddressEmpty={isTerreiroAddressEmpty}
          isLoading={isPending}
          onConfirm={(nextLocation: EventChatLocationState) =>
            handleStructuredSelection("location", nextLocation)
          }
        />
      );
    }
    return null;
  }, [
    activeField,
    formState.insumos,
    formState.location,
    formState.memberIds,
    formState.minConfirmedMembers,
    handleStructuredSelection,
    insumoOptions,
    isPending,
    isPersonalProfile,
    isTerreiroAddressEmpty,
    memberOptions,
  ]);

  const editableFieldOptions = useMemo<FormChatEditableFieldOption[]>(() => {
    const read = (key: keyof ActivityChatSummary) =>
      summary?.[key] ?? formState[key as keyof FormChatState];

    const memberIds = toStringList(read("memberIds"));
    const members = memberIds.map((id, index) => {
      const option = (memberOptions as ActivityChatMemberOption[]).find(
        (member) => member.id === id,
      );
      return {
        name: option?.name ?? summary?.memberNames?.[index] ?? "Participante",
        src: option?.profilePictureUrl ?? null,
      };
    });

    const insumos = toInsumoSelection(read("insumos")).map((selection, index) => {
      const option = insumoOptions.find((item) => item.id === selection.id);
      const name = option?.name ?? summary?.insumoLabels?.[index] ?? "Material";
      return {
        name,
        label: option ? `${name} (${selection.quantity})` : name,
        src: option?.imageUrl ?? null,
      };
    });

    const qrCheckinEnabled = read("qrCheckinEnabled");
    const location = isActivityLocationPayload(summary?.location)
      ? summary.location
      : isActivityLocationPayload(formState.location)
        ? formState.location
        : undefined;
    const isInTerreiro =
      summary?.isInTerreiro ?? formState.isInTerreiro ?? location?.isInTerreiro;
    const hiddenLocationFields = resolveHiddenActivityLocationFields({
      isInTerreiro,
      isPersonalProfile,
    });

    const currentValueByKey: Record<string, string | undefined> = {
      title: formatChatText(read("title")),
      description: formatChatText(read("description")),
      activityType: formatChatActivityType(
        read("activityType") ?? resolvedActivityType,
      ),
      isTemplate: formatChatYesNo(read("isTemplate")),
      date: formatChatDate(read("date")),
      startTime: formatChatTime(read("startTime")),
      endTime: formatChatTime(read("endTime")),
      qrCheckinEnabled: formatChatToggle(qrCheckinEnabled),
      checkinStartWindowMinutes: formatChatMinutes(read("checkinStartWindowMinutes")),
      checkinEndWindowMinutes: formatChatMinutes(read("checkinEndWindowMinutes")),
      isInTerreiro: formatChatYesNo(isInTerreiro),
      location: location
        ? formatActivityChatLocationLabel(location)
        : formatChatText(formState.location),
      memberIds: members.length
        ? members.map((member) => member.name).join(", ")
        : undefined,
      minConfirmedMembers: formatChatText(read("minConfirmedMembers")),
      insumos: insumos.length
        ? insumos.map((insumo) => insumo.label).join(", ")
        : undefined,
    };

    const fields = editableFields.filter(
      (field) =>
        !(modelTargetId && field.key === "isTemplate") &&
        !(isPersonalProfile && PERSONAL_HIDDEN_EDIT_FIELDS.has(field.key)) &&
        !hiddenLocationFields.has(field.key) &&
        !(qrCheckinEnabled !== true && CHECKIN_WINDOW_EDIT_FIELDS.has(field.key)),
    );

    return fields.map((field) => ({
      key: field.key,
      label: field.label,
      currentValue: currentValueByKey[field.key],
      avatars:
        field.key === "memberIds"
          ? members.map((member) => ({ src: member.src, alt: member.name }))
          : field.key === "insumos"
            ? insumos.map((insumo) => ({ src: insumo.src, alt: insumo.name }))
            : undefined,
    }));
  }, [
    editableFields,
    formState,
    insumoOptions,
    isPersonalProfile,
    memberOptions,
    modelTargetId,
    resolvedActivityType,
    summary,
  ]);

  const summaryCard = useMemo(() => {
    if (!summary) return null;
    return (
      <ActivityFormChatSummaryCard
        summary={summary}
        confirmLabel={
          isEditMode ? "Salvar alterações" : `Criar ${wording.singular}`
        }
        isLoading={isPending}
        descriptionAutoGenerated={descriptionAutoGenerated}
        onConfirm={handleConfirmActivity}
        onEdit={onEdit}
      />
    );
  }, [
    descriptionAutoGenerated,
    handleConfirmActivity,
    isEditMode,
    isPending,
    onEdit,
    summary,
    wording.singular,
  ]);

  return {
    activityReferenceData,
    sendActivityRequest,
    handleConfirmActivity,
    fieldPanel,
    summaryCard,
    stepOrder: ACTIVITY_STEP_ORDER,
    editableFields,
    editableFieldOptions,
    isPending,
    transcribeAudio: async (audio: Blob) => {
      if (terreiroId) {
        return (await formChatRepository.transcribeAudio(terreiroId, audio))
          .transcript;
      }
      if (mediumId) {
        return (
          await formChatRepository.transcribePersonalAudio(mediumId, audio)
        ).transcript;
      }
      throw new Error("Owner scope required");
    },
  };
}
